import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "hb_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {}
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((product, opts = {}) => {
    const variant = {
      length: opts.length ?? product.length ?? null,
      texture: opts.texture ?? product.texture ?? null,
      color: opts.color ?? product.color ?? null,
    };
    const key = `${product.product_id}|${variant.length}|${variant.texture}|${variant.color}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + (opts.quantity || 1) } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.product_id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          image_url: product.image_url,
          quantity: opts.quantity || 1,
          ...variant,
        },
      ];
    });
    toast.success(`${product.name} added to bag`);
    setDrawerOpen(true);
  }, []);

  const remove = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const updateQty = useCallback((key, qty) => {
    if (qty <= 0) return remove(key);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: qty } : i)));
  }, [remove]);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + (i.price || 0) * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <CartContext.Provider
      value={{ items, add, remove, updateQty, clear, subtotal, count, drawerOpen, setDrawerOpen }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
